import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { FaEnvelopeOpenText } from 'react-icons/fa';
import { customToast } from '../utils/customToast';
import { isPaymentsConfigured, resendActivationSelfServe } from '../services/paymentsApi';
import { loadSignup } from './selfServeSignup';
import '../site.css';
import V3Head from '../site-v3/V3Head';

/**
 * Paystack redirects here (CHECKOUT_CALLBACK_URL) after the user finishes paying.
 * Activation is driven by the webhook, not this page: on `charge.success` the
 * pear-payments backend activates the org and emails the sign-in link. So this
 * page's only job is to tell the user to go check their work email.
 *
 * Paystack appends `?reference=…&trxref=…`; we surface the reference for support,
 * but never trust it for activation (that's the signed webhook's job).
 */
export default function BillingReturn() {
  const params = new URLSearchParams(window.location.search);
  const reference = params.get('reference') || params.get('trxref') || '';

  const { email, payload } = loadSignup();
  const orgCode = payload?.code || '';
  const canResend = isPaymentsConfigured() && !!orgCode && !!email;

  const [sending, setSending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const resendCooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => () => {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
  }, []);

  function startResendCooldown() {
    if (resendCooldownRef.current) clearInterval(resendCooldownRef.current);
    setResendCooldown(60);
    resendCooldownRef.current = setInterval(() => {
      setResendCooldown((s) => {
        if (s <= 1) {
          if (resendCooldownRef.current) {
            clearInterval(resendCooldownRef.current);
            resendCooldownRef.current = null;
          }
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  async function handleResend() {
    if (!canResend || resendCooldown > 0 || sending) return;
    setSending(true);
    const result = await resendActivationSelfServe(orgCode, email);
    setSending(false);
    if (result.ok) {
      customToast.show(
        'Link sent again — check your inbox for an email from Trasse (not a generic Firebase message).',
        'success',
      );
      startResendCooldown();
    } else {
      customToast.show(result.message || 'Could not resend the link. Please try again.', 'error');
    }
  }

  return (
    <div className="site-theme v3" style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <V3Head />
      <main style={{ flex: 1, display: 'grid', placeItems: 'center', padding: '40px 20px' }}>
        <div
          style={{
            width: '100%', maxWidth: 460, background: '#fff', borderRadius: 18,
            boxShadow: '0 18px 50px rgba(20,30,60,.10)', border: '1px solid var(--line,#e7ecf3)',
            padding: '36px 30px', textAlign: 'center',
          }}
        >
          <div
            style={{
              width: 56, height: 56, borderRadius: 14, margin: '0 auto 18px',
              display: 'grid', placeItems: 'center', background: '#E2F5F8', color: '#00707F', fontSize: 24,
            }}
          >
            <FaEnvelopeOpenText />
          </div>
          <h1 style={{ margin: '0 0 8px', fontSize: 22, color: '#0f172a' }}>Payment received — check your email</h1>
          <p style={{ margin: '0 0 18px', color: '#475569', fontSize: 14, lineHeight: 1.6 }}>
            We&apos;ve sent a secure sign-in link to the work email you signed up with
            {email ? <> (<b>{email}</b>)</> : null}.
            Open it to set your password and step into your dashboard — that link is your admin login.
          </p>
          <p style={{ margin: '0 0 22px', color: '#94a3b8', fontSize: 12, lineHeight: 1.5 }}>
            It can take a minute to arrive. Check spam if you don&apos;t see it, and open it on this
            same device for the smoothest sign-in.
          </p>
          {canResend && (
            <button
              type="button"
              onClick={() => void handleResend()}
              disabled={sending || resendCooldown > 0}
              style={{
                display: 'inline-block', background: '#0F1419', color: '#fff', border: 'none',
                fontWeight: 700, fontSize: 14, padding: '12px 22px', borderRadius: 10,
                marginBottom: 12, cursor: sending || resendCooldown > 0 ? 'default' : 'pointer',
                opacity: sending || resendCooldown > 0 ? 0.7 : 1,
              }}
            >
              {sending ? 'Sending…' : resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend link'}
            </button>
          )}
          <div>
            <Link
              to="/"
              style={{
                display: 'inline-block', background: canResend ? '#fff' : '#0F1419',
                color: canResend ? '#0F1419' : '#fff', textDecoration: 'none',
                fontWeight: 700, fontSize: 14, padding: '12px 22px', borderRadius: 10,
                border: canResend ? '1px solid #c7d9ff' : 'none',
              }}
            >
              Back to home
            </Link>
          </div>
          {reference && (
            <p style={{ margin: '18px 0 0', color: '#cbd5e1', fontSize: 11 }}>Ref: {reference}</p>
          )}
        </div>
      </main>
    </div>
  );
}
